import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { useMessageNotifications } from 'contexts/MessageNotificationContext';
import Navbar from 'components/Header/Navbar';
import Footer from 'components/Footer';
import SbPageHeader from 'components/layout/SbPageHeader';
import SbEmptyState from 'components/layout/SbEmptyState';
import ChatPanel from 'components/support/ChatPanel';
import ListingContextBanner from 'components/chat/ListingContextBanner';
import BuyerVerifyButton from 'components/Listing/BuyerVerifyButton';
import RatingModal from 'components/Rating/RatingModal';
import { conversationService } from 'api/services/ConversationService';
import { moderationService } from 'api/services/ModerationService';
import ChatUserComplaintModal from 'components/chat/ChatUserComplaintModal';
import Button from 'components/Button';
import { fetchPeerDisplayNames, peerDisplayNameFromMap } from 'utils/peerDisplayName';
import type { ConversationViewModel } from 'api/types/conversation/ConversationViewModel';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { formatRelativeTime } from 'utils/formatRelativeTime';
import { USER_UNBLOCKED_EVENT, type UserUnblockedEventDetail } from 'utils/moderationBlockEvents';

interface ConversationListItem extends ConversationViewModel {
  otherParticipantName: string;
}

const LIST_REFETCH_INTERVAL_MS = 30000;

type ConversationListPanelMode = 'inbox' | 'blocked';

const Mesajlar: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deepLinkConvId = searchParams.get('conversationId');
  const draftMessageParam = searchParams.get('draftMessage');
  const { user } = useAuth();
  const { markConversationRead, setBadgeExcludeConversationId, refresh: refreshMessageNotifications } =
    useMessageNotifications();
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState<ConversationListItem | null>(null);
  const [ratingTarget, setRatingTarget] = useState<{ soldListingId: string; listingTitle: string } | null>(null);
  const [otherParticipantId, setOtherParticipantId] = useState<string | null>(null);
  const [listPanelMode, setListPanelMode] = useState<ConversationListPanelMode>('inbox');
  const [complaintModalOpen, setComplaintModalOpen] = useState(false);

  useEffect(() => {
    if (!selected || !user?.id) {
      setOtherParticipantId(null);
      return;
    }
    const otherId = selected.senderId === user.id ? selected.receiverId : selected.senderId;
    setOtherParticipantId(otherId ?? null);
  }, [selected, user?.id]);

  const loadConversations = useCallback(async (options?: { panelMode?: ConversationListPanelMode }) => {
    if (!user?.id) return;
    const panelMode = options?.panelMode ?? listPanelMode;
    setLoading(true);
    setError(null);
    try {
      const result = await conversationService.getMyConversations({
        pageNumber: 1,
        pageSize: 50,
        withUnread: true,
        blockedOnly: panelMode === 'blocked',
      });
      const list = (result.data ?? []) as ConversationViewModel[];
      const peerIds = list.map((c) =>
        c.senderId === user.id ? c.receiverId : c.senderId,
      );
      const nameMap = await fetchPeerDisplayNames(peerIds);
      const withNames: ConversationListItem[] = list.map((c) => {
        const otherId = c.senderId === user.id ? c.receiverId : c.senderId;
        return {
          ...c,
          otherParticipantName: peerDisplayNameFromMap(otherId, nameMap),
        };
      });
      setConversations(withNames);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Konuşmalar yüklenemedi.');
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, listPanelMode]);

  const applyUserUnblocked = useCallback(
    async (unblockedUserId: string) => {
      if (!user?.id) return;
      setSelected((current) => {
        if (!current) return null;
        const otherId = current.senderId === user.id ? current.receiverId : current.senderId;
        return otherId === unblockedUserId ? null : current;
      });
      setListPanelMode('inbox');
      await loadConversations({ panelMode: 'inbox' });
      void refreshMessageNotifications();
    },
    [user?.id, loadConversations, refreshMessageNotifications],
  );

  const handleBlockUser = async () => {
    if (!otherParticipantId) return;
    if (!window.confirm('Bu kullanıcıyı engellemek istediğinize emin misiniz?')) return;
    const res = await moderationService.blockUser({ blockedUserId: otherParticipantId });
    window.alert(res.message ?? (res.isSuccess ? 'Kullanıcı engellendi.' : 'İşlem başarısız.'));
    if (res.isSuccess) {
      setSelected(null);
      await loadConversations();
    }
  };

  const handleUnblockUser = async () => {
    if (!otherParticipantId) return;
    if (!window.confirm('Bu kullanıcının engelini kaldırmak istediğinize emin misiniz?')) return;
    const res = await moderationService.unblockUser(otherParticipantId);
    if (!res.isSuccess) {
      window.alert(res.message ?? 'Engel kaldırılamadı.');
      return;
    }
    await applyUserUnblocked(otherParticipantId);
  };

  useEffect(() => {
    if (user?.id) {
      loadConversations();
    } else {
      setLoading(false);
      navigate('/giris', { replace: true });
    }
  }, [user?.id, navigate, loadConversations]);

  useEffect(() => {
    if (!user?.id) return;
    const interval = setInterval(() => {
      void loadConversations();
    }, LIST_REFETCH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user?.id, loadConversations]);

  useEffect(() => {
    const onUserUnblocked = (event: Event) => {
      const detail = (event as CustomEvent<UserUnblockedEventDetail>).detail;
      if (!detail?.userId) return;
      void applyUserUnblocked(detail.userId);
    };
    window.addEventListener(USER_UNBLOCKED_EVENT, onUserUnblocked);
    return () => window.removeEventListener(USER_UNBLOCKED_EVENT, onUserUnblocked);
  }, [applyUserUnblocked]);

  useEffect(() => {
    if (!selected) return;
    const stillThere = conversations.some((c) => c.id === selected.id);
    if (!stillThere) setSelected(null);
  }, [conversations, selected]);

  // Deep-link: open the conversation identified by the `?conversationId=…` query string
  // as soon as it's available in the loaded list.
  useEffect(() => {
    if (!deepLinkConvId || selected?.id === deepLinkConvId) return;
    const match = conversations.find((c) => c.id === deepLinkConvId);
    if (match) setSelected(match);
  }, [deepLinkConvId, conversations, selected?.id]);

  // Drive the header letter-icon badge down when a conversation is opened.
  useEffect(() => {
    if (!selected?.id) return;
    void markConversationRead(selected.id);
  }, [selected?.id, markConversationRead]);

  // Header aggregate unread excludes the conversation currently open (per spec 012).
  useEffect(() => {
    setBadgeExcludeConversationId(selected?.id ?? null);
    void refreshMessageNotifications();
  }, [selected?.id, setBadgeExcludeConversationId, refreshMessageNotifications]);

  const filtered = searchQuery.trim()
    ? conversations.filter((c) =>
        c.otherParticipantName.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : conversations;

  if (!user) return null;

  return (
    <div className="min-vh-100" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
      <Navbar />

      <main className="sb-page-container py-3 sb-page-shell">
        <SbPageHeader
          title="Mesajlarım"
          breadcrumb={[
            { to: '/', label: 'Ana Sayfa' },
            { to: '/mesajlar', label: 'Mesajlarım' },
          ]}
        />
        <div className="sb-panel-card message-app-container overflow-hidden">
          <div className="row g-0 flex-md-row flex-column" style={{ minHeight: 'calc(100vh - 180px)' }}>
            {/* Sol - Konuşma listesi */}
            <div
              className={`col-12 col-md-4 border-end d-flex flex-column ${
                selected ? 'd-none d-md-flex' : ''
              }`}
              style={{ minHeight: 200, maxHeight: 'calc(100vh - 180px)', backgroundColor: 'var(--sb-bg)' }}
            >
              <div className="chat-header p-3 border-bottom flex-shrink-0 d-flex align-items-center gap-2">
                {listPanelMode === 'blocked' ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-link text-muted text-decoration-none p-0"
                      aria-label="Mesajlarıma dön"
                      onClick={() => {
                        setListPanelMode('inbox');
                        setSelected(null);
                        setConversations([]);
                        void loadConversations({ panelMode: 'inbox' });
                      }}
                    >
                      <i className="fas fa-arrow-left" />
                    </button>
                    <h5 className="mb-0 flex-grow-1">Engellenmiş Sohbetler</h5>
                  </>
                ) : (
                  <>
                    <h5 className="mb-0 flex-grow-1">Mesajlarım</h5>
                    <div className="dropdown">
                      <Button
                        className="btn btn-light btn-sm rounded-circle"
                        type="button"
                        id="mesajlarListMenu"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        title="Liste menüsü"
                      >
                        <i className="fas fa-ellipsis-v" />
                      </Button>
                      <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="mesajlarListMenu">
                        <li>
                          <button
                            type="button"
                            className="dropdown-item py-2"
                            onClick={() => {
                              setListPanelMode('blocked');
                              setSelected(null);
                              setConversations([]);
                              void loadConversations({ panelMode: 'blocked' });
                            }}
                          >
                            <i className="fas fa-ban me-2" />
                            Engellenmiş Sohbetler
                          </button>
                        </li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
              <div className="p-2 p-md-3 border-bottom flex-shrink-0">
                <div className="input-group input-group-sm">
                  <span className="input-group-text border-end-0" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
                    <i className="fas fa-search text-muted" />
                  </span>
                  <input
                    className="form-control border-start-0"
                    style={{ backgroundColor: 'var(--sb-bg-muted)' }}
                    placeholder="Mesajlarda ara..."
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="overflow-auto flex-grow-1">
                {loading ? (
                  <div className="d-flex align-items-center justify-content-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Yükleniyor...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="p-3 text-danger small">{error}</div>
                ) : filtered.length > 0 ? (
                  filtered.map((conv) => (
                    <button
                      key={conv.id}
                      type="button"
                      onClick={() => setSelected(conv)}
                      className={`chat-user w-100 p-3 border-bottom border-start-0 border-end-0 text-start text-dark bg-transparent ${
                        selected?.id === conv.id ? 'active' : ''
                      }`}
                    >
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center flex-shrink-0 me-3"
                          style={{ width: 44, height: 44 }}
                        >
                          <i className="fas fa-user" />
                        </div>
                        <div className="flex-grow-1 overflow-hidden">
                          <div className="d-flex justify-content-between align-items-center gap-2">
                            <h6 className="mb-0 text-truncate">{conv.otherParticipantName}</h6>
                            <small className="text-muted flex-shrink-0">
                              {formatRelativeTime(conv.lastMessageAt)}
                            </small>
                          </div>
                          <p className="mb-0 small text-muted text-truncate">
                            {conv.isClosed
                              ? 'Sohbet kapatıldı'
                              : conv.lastMessagePreview || 'Henüz mesaj yok'}
                          </p>
                        </div>
                        {(conv.unreadMessageCount ?? 0) > 0 && (
                          <span className="badge bg-primary rounded-pill flex-shrink-0">
                            {conv.unreadMessageCount! > 99 ? '99+' : conv.unreadMessageCount}
                          </span>
                        )}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="text-center p-4">
                    <i className="far fa-comments fa-2x text-muted mb-3" />
                    <p className="mb-0 text-muted small">
                      {searchQuery.trim()
                        ? 'Aramanızla eşleşen konuşma yok.'
                        : listPanelMode === 'blocked'
                          ? 'Engellenmiş sohbetiniz yok.'
                          : 'Henüz konuşmanız yok.'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Sağ - Sohbet veya boş durum. When selected: fixed height so flex chain works and input stays at bottom. */}
            <div
              className="col-12 col-md-8 d-flex flex-column chat-right-column"
              style={
                selected
                  ? { height: 'calc(100vh - 180px)', minHeight: 320, backgroundColor: 'var(--sb-bg-muted)' }
                  : { minHeight: 280, backgroundColor: 'var(--sb-bg-muted)' }
              }
            >
              {selected ? (
                <>
                  <div className="p-2 p-md-3 border-bottom flex-shrink-0 d-flex align-items-center gap-2" style={{ backgroundColor: 'var(--sb-bg)' }}>
                    <button
                      type="button"
                      className="btn btn-link text-muted text-decoration-none p-0 me-2 d-md-none"
                      onClick={() => setSelected(null)}
                      aria-label="Listeye dön"
                    >
                      <i className="fas fa-arrow-left" />
                    </button>
                    <div className="flex-grow-1 min-width-0">
                      <h6 className="mb-0 text-truncate">{selected.otherParticipantName}</h6>
                    </div>
                    {otherParticipantId && user.id !== otherParticipantId && (
                      <div className="dropdown">
                        <Button
                          className="btn btn-light btn-sm rounded-circle"
                          type="button"
                          id="mesajlarConversationMenu"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                          title="Menü"
                        >
                          <i className="fas fa-ellipsis-v" />
                        </Button>
                        <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="mesajlarConversationMenu">
                          {listPanelMode === 'blocked' ? (
                            <li>
                              <button
                                type="button"
                                className="dropdown-item py-2"
                                onClick={() => void handleUnblockUser()}
                              >
                                <i className="fas fa-unlock me-2" />
                                Engeli Kaldır
                              </button>
                            </li>
                          ) : (
                            <>
                              <li>
                                <button
                                  type="button"
                                  className="dropdown-item py-2"
                                  onClick={() => void handleBlockUser()}
                                >
                                  <i className="fas fa-ban me-2" />
                                  Engelle
                                </button>
                              </li>
                              <li>
                                <button
                                  type="button"
                                  className="dropdown-item py-2"
                                  onClick={() => setComplaintModalOpen(true)}
                                >
                                  <i className="fas fa-flag me-2" />
                                  Şikayet Et
                                </button>
                              </li>
                            </>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                  <ListingContextBanner summary={selected.listingSummary ?? null} />
                  {selected.listingSummary?.id && (selected.listingSummary.soldListing ?? null) && (
                    <div className="px-2 px-md-3 pt-2 d-flex flex-wrap gap-2">
                      <BuyerVerifyButton
                        listingId={selected.listingSummary.id}
                        currentUserId={user.id}
                        soldListingSummary={selected.listingSummary.soldListing ?? null}
                        onVerified={(updated) =>
                          setSelected((prev) =>
                            prev && prev.listingSummary
                              ? {
                                  ...prev,
                                  listingSummary: {
                                    ...prev.listingSummary,
                                    soldListing: updated,
                                  },
                                }
                              : prev,
                          )
                        }
                      />
                      {selected.listingSummary.soldListing
                        && user.id === selected.listingSummary.soldListing.soldToUserId
                        && selected.listingSummary.soldListing.completedAt != null
                        && !selected.listingSummary.soldListing.hasRating && (
                        <button
                          type="button"
                          className="btn btn-warning"
                          onClick={() => setRatingTarget({
                            soldListingId: selected.listingSummary!.soldListing!.id,
                            listingTitle: selected.listingSummary!.title,
                          })}
                        >
                          <i className="fas fa-star me-2" />
                          Satıcıyı Değerlendir
                        </button>
                      )}
                    </div>
                  )}
                  <div className="flex-grow-1 min-height-0 p-2 p-md-3 d-flex flex-column overflow-hidden">
                    <ChatPanel
                      key={selected.id}
                      conversationId={selected.id}
                      currentUserId={user.id}
                      isReadOnly={selected.isClosed ?? false}
                      title=""
                      fillHeight
                      initialComposerText={
                        selected.id === deepLinkConvId ? draftMessageParam : null
                      }
                    />
                  </div>
                </>
              ) : (
                <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 py-4 px-3">
                  <SbEmptyState
                    iconClass="far fa-comment-dots"
                    title="Henüz Konuşmanız Yok"
                    description="Satıcılar veya alıcılarla başlattığınız sohbetler burada listelenir. İlan detay sayfalarından mesaj gönderebilirsiniz."
                    actionLabel="İlanlara Göz At"
                    actionTo="/"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .message-app-container { border-radius: 0.5rem; }
        .chat-user:hover { background-color: var(--sb-bg-muted) !important; }
        .chat-user.active { background-color: var(--sb-dropdown-hover-bg) !important; border-left: 3px solid #0d6efd; }
        .chat-user:focus { outline: none; box-shadow: none; }
        .chat-right-column { position: relative; }
        .chat-input-form { position: sticky; bottom: 0; z-index: 2; }
        @media (min-width: 768px) {
          .message-app-container { min-height: calc(100vh - 180px); }
        }
        @media (max-width: 767.98px) {
          .chat-right-column.chat-right-column { height: calc(100vh - 140px) !important; min-height: 280px; }
        }
      `}</style>
      {complaintModalOpen && otherParticipantId && user.id !== otherParticipantId && (
        <ChatUserComplaintModal
          reportedUserId={otherParticipantId}
          onClose={() => setComplaintModalOpen(false)}
          onSubmitted={() => {
            window.alert('Şikayetiniz alındı ve kullanıcı engellendi.');
            setComplaintModalOpen(false);
            setSelected(null);
            void loadConversations();
          }}
        />
      )}
      {ratingTarget && (
        <RatingModal
          soldListingId={ratingTarget.soldListingId}
          listingTitle={ratingTarget.listingTitle}
          onSubmitted={() =>
            setSelected((prev) =>
              prev && prev.listingSummary?.soldListing
                ? {
                    ...prev,
                    listingSummary: {
                      ...prev.listingSummary,
                      soldListing: { ...prev.listingSummary.soldListing, hasRating: true },
                    },
                  }
                : prev,
            )
          }
          onClose={() => setRatingTarget(null)}
        />
      )}
      <Footer />
    </div>
  );
};

export default Mesajlar;
